import cx from 'clsx';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type {
  ClientDetails as ClientDetailsType,
  Document,
  Logo as LogoType,
  MerchantDetails,
  RelatedInvoice,
  RelatedQuote,
} from '../types';
import { usePdfPreviewContext } from '../pdf-preview-context';
import { Addresses } from './internals/addresses.tsx';
import { ClientDetails } from './internals/client-details.tsx';
import { DocumentDetails } from './internals/document-details.tsx';
import { Logo } from './internals/logo.tsx';
import { MerchantInformation } from './internals/merchant-information';
import { Reason } from './internals/reason.tsx';
import { Title } from './internals/title';
import styles from './header.module.css';

interface HeaderProps {
  document: Document;
  merchant: MerchantDetails;
  client?: ClientDetailsType;
  logo?: LogoType;
  relatedInvoice?: RelatedInvoice;
  relatedQuote?: RelatedQuote;
  shouldDisplaySpanishEInvoicingQrCode?: boolean;
}

export function Header({
  client,
  document,
  logo,
  merchant,
  relatedInvoice,
  relatedQuote,
  shouldDisplaySpanishEInvoicingQrCode = false,
}: HeaderProps): ReactNode {
  const { variant } = usePdfPreviewContext();

  if (variant === 'DE') {
    return (
      <header className={cx(styles.header, styles.DE)}>
        <div className={styles.title}>
          <Title />
          <Logo logo={logo} />
        </div>
        <MerchantInformation merchant={merchant} />
        <div className={styles['sub-header']}>
          <div>
            <Addresses client={client} merchant={merchant} />
          </div>
          <div className={styles['full-details']}>
            <ClientDetails client={client} />
            <DocumentDetails
              document={document}
              relatedInvoice={relatedInvoice}
              relatedQuote={relatedQuote}
            />
          </div>
        </div>
        <Reason reason={document.reason} />
      </header>
    );
  }

  return (
    <>
      {Boolean(shouldDisplaySpanishEInvoicingQrCode) && (
        <div className={styles['spain-header']} data-testid="es-qr-code-placeholder">
          <div className={styles['qr-code-container']}>
            <div data-testid="es-qr-code-label-1">
              <FormattedMessage id="receivable-invoices.einvoicing.spain.pdf.label-1" />
            </div>
            <div className={styles['qr-code-placeholder']} />
            <div data-testid="es-qr-code-label-2">
              <FormattedMessage id="receivable-invoices.einvoicing.spain.pdf.label-2" />
            </div>
          </div>
        </div>
      )}
      <header className={styles.header}>
        <div className={styles.title}>
          <div>
            <Title />
            <DocumentDetails
              document={document}
              relatedInvoice={relatedInvoice}
              relatedQuote={relatedQuote}
            />
          </div>
          <Logo logo={logo} />
        </div>
        <div className={styles['sub-header']}>
          <Addresses client={client} merchant={merchant} />
        </div>
        <Reason reason={document.reason} />
      </header>
    </>
  );
}
